import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import gettingStartedBg from "assets/images/onboarding/getting-started-bg.webp";
import onboardingRocket from "assets/onboardingRocket.gif";
import { CheckIcon } from "components";
import { DRAWER_STEPS } from "./drawerSteps";
import { OnboardingDrawerStepItem } from "./OnboardingDrawerStepItem";
import type { DrawerStepKey } from "./types";

interface OnboardingDrawerSidebarProps {
  activeStepKey: DrawerStepKey;
  completedStepKeys: Set<DrawerStepKey>;
  unlockedStepKeys: Set<DrawerStepKey>;
  onStepClick: (key: DrawerStepKey) => void;
  onMarkAllComplete: () => void;
  isMarkingAll: boolean;
}

const gettingStartedCardSx = {
  padding: "24px",
  borderRadius: "8px",
  backgroundImage: `url(${gettingStartedBg})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.2)",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
} as const;

export const OnboardingDrawerSidebar = ({
  activeStepKey,
  completedStepKeys,
  unlockedStepKeys,
  onStepClick,
  onMarkAllComplete,
  isMarkingAll,
}: OnboardingDrawerSidebarProps) => {
  const theme = useTheme();
  const completedCount = completedStepKeys.size;
  const totalCount = DRAWER_STEPS.length;
  const progressPercent = (completedCount / totalCount) * 100;

  return (
    <Stack
      sx={{
        width: "320px",
        height: "100%",
        padding: "24px",
        gap: "24px",
        backgroundColor: "#2C303A",
        overflowY: "auto",
      }}
    >
      {/* Getting Started Card */}
      <Box sx={gettingStartedCardSx}>
        {/* Top section: rocket, title, description */}
        <Stack gap="8px">
          <img
            src={onboardingRocket}
            alt="Getting started rocket"
            width="32px"
            height="32px"
          />
          <Typography
            variant="h5"
            sx={{ color: theme.palette.darkBg.high, fontWeight: 600 }}
          >
            Getting started
          </Typography>
          <Typography
            variant="body-small"
            sx={{ color: theme.palette.darkBg.medium }}
          >
            Complete these steps to unlock the full power of DeskDay
          </Typography>
        </Stack>

        {/* Progress section */}
        <Stack gap="8px">
          <Stack direction="row" justifyContent="space-between">
            <Typography
              variant="body-xsmall"
              sx={{ color: theme.palette.darkBg.medium }}
            >
              Your progress
            </Typography>
            <Typography
              variant="body-xsmall"
              sx={{ color: theme.palette.darkBg.medium }}
            >
              {completedCount} of {totalCount} completed
            </Typography>
          </Stack>
          <Box
            sx={{
              height: "6px",
              borderRadius: "3px",
              backgroundColor: "rgba(255, 255, 255, 0.15)",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${progressPercent}%`,
                borderRadius: "3px",
                backgroundColor: "#5EBB70",
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        </Stack>
      </Box>
      {/* Divider */}
      <Box
        sx={{
          height: "1px",
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          width: "100%",
          flexShrink: 0,
        }}
      />
      {/* Step List */}
      <Stack gap="8px">
        {DRAWER_STEPS.map((step) => {
          const isLocked = !unlockedStepKeys.has(step.key);
          return (
            <OnboardingDrawerStepItem
              key={step.key}
              step={step}
              isActive={activeStepKey === step.key}
              isCompleted={completedStepKeys.has(step.key)}
              isLocked={isLocked}
              onClick={() => {
                if (isLocked) return;
                onStepClick(step.key);
              }}
            />
          );
        })}
      </Stack>
      {/* Mark all as complete */}
      <Button
        variant="outlined"
        fullWidth
        disabled={isMarkingAll || completedCount === totalCount}
        onClick={onMarkAllComplete}
        endIcon={
          isMarkingAll ? (
            <CircularProgress size={16} sx={{ color: "inherit" }} />
          ) : (
            <CheckIcon sx={{ width: "16px", height: "16px" }} />
          )
        }
        sx={{
          ...theme.typography["button-label"],
          lineHeight: "16px",
          color:
            completedCount === totalCount
              ? theme.palette.primitive.system.green[500]
              : "#FFFFFF",
          borderColor:
            completedCount === totalCount
              ? theme.palette.primitive.system.green[500]
              : "#FFFFFF",
          py: "8px",
          borderRadius: "6px",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            borderColor: "#FFFFFF",
          },
          "&.Mui-disabled": {
            color:
              completedCount === totalCount
                ? theme.palette.primitive.system.green[500]
                : "rgba(255, 255, 255, 0.4)",
            borderColor:
              completedCount === totalCount
                ? theme.palette.primitive.system.green[500]
                : "rgba(255, 255, 255, 0.4)",
            backgroundColor:
              completedCount === totalCount ? "#1e3c23" : "transparent",
          },
        }}
      >
        {completedCount === totalCount ? "Completed" : "Mark all as complete"}
      </Button>
    </Stack>
  );
};
